import classNames from "classnames";
import { AnimatePresence } from "framer-motion";
import React, { useRef, useState } from "react";
import { CloseButton } from "../CloseButton";
import { FadeAndCollapseOnExit } from "../FadeAndCollapseOnExit";
import "./notification.scss";
import { getWindow } from "../../utils";

export type TNotificationType = "success" | "info" | "warning" | "error";

export type TNotification = {
    type: TNotificationType;
    children: React.ReactNode;
    onClose?: () => void;
    show?: boolean;
    className?: string;
    disabled?: boolean;
};

export const Notification = ({ type, children, onClose, show = true, className, disabled = false }: TNotification) => {
    const [open, setOpen] = useState(true);
    const ref = useRef<HTMLDivElement>(null);
    const texts = getWindow().__TEXTS__;
    const errorText = texts?.["error-notification"] || "Error notification";
    const warningText = texts?.["warning-notification"] || "Warning notification";
    const successText = texts?.["success-notification"] || "Success notification";
    const infoText = texts?.["info-notification"] || "Information notification";
    const notificationText = texts?.["notification"] || "Notification";

    if (!show) {
        return null;
    }

    const handleClose = () => {
        setOpen(false);
        onClose?.();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape" && !disabled) {
            handleClose();
        }
    };

    const getAriaRole = () => {
        switch (type) {
            case "error":
                return "alert";
            case "warning":
                return "alert";
            default:
                return "status";
        }
    };

    const getDefaultAriaLabel = () => {
        switch (type) {
            case "error":
                return errorText;
            case "warning":
                return warningText;
            case "success":
                return successText;
            case "info":
                return infoText;
            default:
                return notificationText;
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <FadeAndCollapseOnExit fadeDuration={0.3} collapseDuration={0.3}>
                    <div
                        ref={ref}
                        className={classNames(`c-notification c-notification--${type}`, className, {
                            "c-notification--disabled": disabled,
                        })}
                        role={getAriaRole()}
                        aria-label={getDefaultAriaLabel()}
                        aria-live={type === "error" ? "assertive" : "polite"}
                        aria-atomic="true"
                        tabIndex={0}
                        onKeyDown={handleKeyDown}
                    >
                        <div className="c-notification__indicator" />
                        <div className="c-notification__content">{children}</div>
                        <CloseButton
                            onClick={handleClose}
                            icon="close-circle"
                            iconSize={18}
                            className="c-notification__close-btn"
                        />
                    </div>
                </FadeAndCollapseOnExit>
            )}
        </AnimatePresence>
    );
};
